/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Modal, { type IInternalModalAttrs } from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import ArticleRating from './ArticleRating';
import { utils } from '@glowingblue-orell-thalia-common';
const { Auth } = utils;
import type Mithril from 'mithril';
import type Discussion from 'flarum/common/models/Discussion';
import type Review from '../models/Review';

// Optional, as core's own modals declare their attrs, to stay assignable to `UnsafeModalClass`.
export interface EditReviewModalAttrs extends IInternalModalAttrs {
	/** The review discussion being edited. Its `review` relation is always loaded. */
	discussion?: Discussion;
}

export default class EditReviewModal extends Modal<EditReviewModalAttrs> {
	review!: Review;

	rating: Stream<number>;

	/** `Modal` declares this a plain boolean; this modal keeps a Stream in the slot instead. */
	loading: Stream<boolean>;

	oninit(vnode: Mithril.Vnode<EditReviewModalAttrs, this>) {
		super.oninit(vnode);

		// Only opened from a review discussion's own controls.
		const review = this.attrs.discussion!.review();

		if (!review) {
			throw new Error('EditReviewModal was opened for a discussion that carries no review.');
		}

		this.review = review;

		this.rating = Stream(this.review.rating());

		this.loading = Stream(false);
	}

	className() {
		return 'EditReviewModal';
	}

	title() {
		return app.translator.trans('glowingblue-orell-rezensionen.forum.edit_review.title');
	}

	content() {
		return [
			<div className="Modal-body">
				<div className="EditReviewModal-form">
					<div className="Form-group">
						<ArticleRating onChange={this.rating} value={this.rating()} />
					</div>

					<div className="Form-group">
						<Button type="submit" className="Button Button--primary" loading={this.loading()} disabled={this.loading()}>
							{app.translator.trans('glowingblue-orell-rezensionen.forum.edit_review.submit')}
						</Button>
					</div>
				</div>
			</div>,
		];
	}

	async onsubmit(e: SubmitEvent) {
		e.preventDefault();
		this.loading(true);

		const cb = async () => await this.review.save({ rating: this.rating() });

		try {
			if (this.review.shouldSync()) {
				await Auth.authenticatedCall(cb);
			} else {
				await cb();
			}
		} catch (error) {
			this.loading(false);
			throw error;
		}

		if (this.review.exportSyncFailed()) {
			app.alerts.show(app.translator.trans('glowingblue-orell-rezensionen.forum.alerts.sync_failed'));
		}

		app.modal.close();

		const article = this.review.article();

		if (article) {
			app.store.find('of_articles', article.id()!).then(m.redraw);
		}
	}
}
